import React, { useState } from 'react'
import { createUseStyles } from 'react-jss'
import { CaseDetail } from '../../../../models/CaseDetail'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { Typography } from '../../../ui/Typography/Typography'
import { Overlay } from '../../../ui/Sidebar'
import { RemindersHeaderInfo } from '../Reminders/Reminders'
import { cardProps, control } from '../../../../theme/reuseables'
import { MergedCaseCard } from './MergedCaseCard'
import { L } from '../../../providers/Lokalise'

type CaseNotesProps = {
    caseDetails: CaseDetail
    onUpdated?: () => void
}

export const useMergedCasesStyles = createUseStyles({
    control: {
        ...control,
    },
    card: {
        ...cardProps.card,
    },
    title: {
        ...cardProps.title,
    },
    actions: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        gap: '10px',
    },
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: '100%',
        paddingBottom: '80px',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        '&.visible': {
            transform: 'translateX(0)',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

interface MergedCasesViewProps {
    caseDetails: CaseDetail
    onUpdated?: () => void
}

export const MergedCasesView: React.FC<MergedCasesViewProps> = ({
    caseDetails,
    onUpdated,
}) => {
    const classes = useMergedCasesStyles()
    return (
        <>
            <RemindersHeaderInfo
                name={caseDetails?.newlyHired.name}
                caseNumber={caseDetails?.caseNumber}
            />
            <div className={`${classes.contentWrapper} visible`}>
                <Typography
                    tag="h2"
                    size="L"
                    align="center"
                    weight="bold"
                    style={{ margin: '.5rem 0 4rem' }}
                >
                    {L('LØN_SAMMENLAGTE_SAGER_SIDEBAR_TITEL')}
                </Typography>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        marginBottom: '2rem',
                    }}
                >
                    {caseDetails?.mergedCaseList?.map((mergedCase) => (
                        <MergedCaseCard
                            mergedCase={mergedCase}
                            key={mergedCase.sagsId}
                            caseDetails={caseDetails}
                            onUpdated={() => {
                                onUpdated?.()
                            }}
                        />
                    ))}
                </div>
            </div>
        </>
    )
}

const MergedCasesOverlay: React.FC<CaseNotesProps> = ({
    caseDetails,
    onUpdated,
}): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false)
    const classes = useMergedCasesStyles()

    return (
        <>
            <div
                style={{
                    width: 'fit-content',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    type="action"
                    onClick={() => setIsOpen(true)}
                    size="xlarge"
                    style={{
                        fontSize: '.9rem',
                        minHeight: '1px',
                        textDecoration: 'underline',
                    }}
                >
                    {L('LØN_SAGS_SAMMENLAGTE_SAGER_SIDEBAR_VIS_ALLE_KNAP')}
                </Button>
            </div>

            <Overlay
                isShowing={isOpen}
                hide={() => {
                    setIsOpen(false)
                }}
            >
                <MergedCasesView
                    caseDetails={caseDetails}
                    onUpdated={onUpdated}
                />
            </Overlay>
        </>
    )
}

export default MergedCasesOverlay
